import React, { useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import classNames from "classnames";
import { State } from "~/lib/pipeline";

export type StepLinkHighlight = "failed" | "running";

interface StepLinkStyleOptions {
  active?: boolean;
  highlight?: StepLinkHighlight;
  disabled?: boolean;
  depth?: number;
}

/**
 * Shared className for step-list interactive items (links, unblock buttons).
 */
export function stepLinkClassName({
  active,
  highlight,
  disabled,
  depth = 0,
}: StepLinkStyleOptions) {
  return twMerge(
    "group/link peer",
    "text-charcoal-700 flex min-w-0 flex-auto items-center gap-1 pl-1.5 pr-3 text-sm no-underline rounded-md hover:text-inherit hover:no-underline focus:text-inherit focus:no-underline focus:outline-none active:no-underline focus-visible:ring-2 focus-visible:ring-inset",
    "hover:bg-gray-100 group-hover:bg-gray-100",
    classNames({
      "[&:not([aria-current=step])]:bg-red-100 [&:not([aria-current=step])]:hover:bg-gray-100 dark:[&:not([aria-current=step])]:bg-red-700/15":
        highlight === "failed",
      "[&:not([aria-current=step])]:bg-orange-100 [&:not([aria-current=step])]:hover:bg-gray-100 dark:[&:not([aria-current=step])]:bg-orange-700/15":
        highlight === "running",
      "relative ring-2 ring-inset ring-purple-600": active,
      "cursor-not-allowed": disabled,
    }),
  );
}

interface Props {
  state?: State | null;
  to: string;
  type?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLAnchorElement>) => void;
  active?: boolean;
  highlight?: StepLinkHighlight;
  depth?: number;
  title?: string;
  disabled?: boolean;
  onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  "data-loaded"?: boolean;
}

/**
 * A themed sidebar link that is scrolled into view when active.
 */
export const StepLink = ({
  to,
  type,
  className,
  children,
  onClick,
  onKeyDown,
  onMouseEnter,
  onMouseLeave,
  active,
  depth = 0,
  highlight,
  title,
  disabled,
  state,
  "data-loaded": dataLoaded,
}: Props) => {
  const track = useTrack();
  const scrollRef = useRef<HTMLAnchorElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      track("Build Sidebar Step Clicked", { type });

      onClick?.(event);

      if (active && !event.metaKey) {
        event.preventDefault();
        navigate(pathname);
        return;
      }
    },
    [active, pathname, track, type, onClick, navigate],
  );

  return (
    <Link
      title={title}
      aria-current={active ? "step" : undefined}
      onClick={handleClick}
      onKeyDown={onKeyDown}
      to={to}
      ref={scrollRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-test-id="step-link"
      data-step-state={state}
      data-loaded={dataLoaded}
      aria-disabled={disabled || undefined}
      className={twMerge(
        "h-full",
        stepLinkClassName({ active, highlight, disabled, depth }),
        "aria-[current=step]:bg-gray-100",
        className,
      )}
    >
      {children}
    </Link>
  );
};
